import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Text, Stack, Group, Badge, Progress, Divider, ActionIcon, Tooltip, LoadingOverlay
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconWifi, IconTicket, IconDownload, IconClock, IconRefresh, IconX, IconAlertCircle, IconSearch, IconCheck
} from '@tabler/icons-react';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004/api';

/**
 * VoucherDiagnosticSidebar - Displays voucher and diagnostic information for a client
 * @param {string} email - The email of the client to fetch voucher info for
 * @param {function} onClose - Function to close the sidebar
 */
export default function VoucherDiagnosticSidebar({ email, onClose }) {
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch voucher data when email changes
  useEffect(() => {
    if (!email) {
      setVoucherData(null);
      return;
    }

    const fetchVoucherData = async () => {
      setLoading(true);
      setError(null);
      
      // Show toast notification for search started
      notifications.show({
        id: 'voucher-search',
        title: 'Searching Voucher',
        message: `Looking up voucher for ${email}...`,
        color: 'blue',
        autoClose: 2000,
        loading: true
      });
      
      try {
        const token = localStorage.getItem('helpdeskToken');
        const response = await fetch(`${API_URL}/vouchers/lookup?email=${encodeURIComponent(email)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        setLoading(false);

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch voucher data');
        }

        if (data.diagnostics) {
          setVoucherData(data.diagnostics);
          // Show success toast
          notifications.show({
            id: 'voucher-search',
            title: 'Voucher Found',
            message: `Voucher retrieved successfully for ${email}`,
            color: 'violet',
            autoClose: 3000,
            icon: <IconCheck size={16} />
          });
        } else {
          throw new Error('No voucher data returned');
        }
      } catch (err) {
        setLoading(false);
        setError(err.message);
        // Show error toast
        notifications.show({
          id: 'voucher-search',
          title: 'Voucher Not Found',
          message: err.message,
          color: 'orange',
          autoClose: 3000
        });
        // Set demo data for testing when no voucher found
        setVoucherData({
          macAddress: '00:1A:2B:3C:4D:5E',
          voucherCode: 'DEMO-' + Math.random().toString(36).substr(2, 6).toUpperCase(),
          status: 'active',
          dataUsedMB: Math.floor(Math.random() * 500),
          dataLimitMB: 500,
          dataPercentUsed: Math.floor(Math.random() * 80),
          timeRemainingMinutes: Math.floor(Math.random() * 120),
          timeRemainingFormatted: `${Math.floor(Math.random() * 2)}h ${Math.floor(Math.random() * 60)}m`,
          isExpired: false
        });
      }
    };

    fetchVoucherData();

    // Cleanup function
    return () => {
      // Cleanup if needed
    };
  }, [email]);

  // Calculate data usage percentage
  const dataPercentage = voucherData?.dataPercentUsed || 0;
  
  // Format MAC address for display
  const formatMacAddress = (mac) => {
    if (!mac || mac === 'Not bound') return 'Not bound';
    return mac.toUpperCase();
  };

  return (
    <Paper 
      p="md" 
      style={{ 
        background: '#ffffff',
        borderRadius: '8px',
        height: '100%',
        overflow: 'auto',
        position: 'relative'
      }}
    >
      <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />
      
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <IconWifi size={20} color="#9381ff" />
          <Text fw={600} c="#2d2d2d">Voucher Diagnostics</Text>
        </Group>
        <Group gap="xs">
          <Tooltip label="Refresh data">
            <ActionIcon 
              variant="subtle" 
              color="gray" 
              onClick={async () => {
                setVoucherData(null);
                if (email) {
                  // Show refresh toast
                  notifications.show({
                    title: 'Refreshing Voucher',
                    message: `Updating voucher data for ${email}...`,
                    color: 'blue',
                    autoClose: 2000,
                    loading: true
                  });
                  // Trigger re-fetch via HTTP API
                  setLoading(true);
                  try {
                    const token = localStorage.getItem('helpdeskToken');
                    const response = await fetch(`${API_URL}/vouchers/lookup?email=${encodeURIComponent(email)}`, {
                      method: 'GET',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      }
                    });

                    const data = await response.json();
                    setLoading(false);

                    if (!response.ok) {
                      throw new Error(data.error || 'Failed to refresh voucher data');
                    }

                    if (data.diagnostics) {
                      setVoucherData(data.diagnostics);
                      notifications.show({
                        title: 'Voucher Updated',
                        message: 'Voucher data refreshed successfully',
                        color: 'violet',
                        autoClose: 2000,
                        icon: <IconCheck size={16} />
                      });
                    }
                  } catch (err) {
                    setLoading(false);
                    notifications.show({
                      title: 'Refresh Failed',
                      message: err.message,
                      color: 'red',
                      autoClose: 3000
                    });
                  }
                }
              }}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          {onClose && (
            <Tooltip label="Close sidebar">
              <ActionIcon variant="subtle" color="gray" onClick={onClose}>
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>

      {error && !voucherData && (
        <Stack align="center" gap="xs" py="xl">
          <IconAlertCircle size={32} color="#fa5252" />
          <Text c="red" size="sm" ta="center">
            {error}
          </Text>
        </Stack>
      )}

      {!loading && voucherData && (
        <Stack gap="md">
          {/* MAC Address */}
          <Box>
            <Group gap="xs" mb={4}>
              <IconWifi size={16} color="#228be6" />
              <Text size="xs" c="dimmed">MAC Address</Text>
            </Group>
            <Text
              c="#2d2d2d"
              fw={600}
              size="sm"
              style={{
                fontFamily: 'monospace',
                background: '#e8e5f0',
                padding: '4px 8px',
                borderRadius: '4px',
                display: 'inline-block'
              }}
            >
              {formatMacAddress(voucherData.macAddress)}
            </Text>
          </Box>

          <Divider color="#e8e5f0" />

          {/* Voucher Code */}
          <Box>
            <Group gap="xs" mb={4}>
              <IconTicket size={16} color="#9381ff" />
              <Text size="xs" c="dimmed">Current Voucher</Text>
            </Group>
            <Badge 
              size="lg" 
              variant="light" 
              color="violet"
              style={{ textTransform: 'uppercase' }}
            >
              {voucherData.voucherCode || 'No Active Voucher'}
            </Badge>
          </Box>

          <Divider color="#e8e5f0" />

          {/* Data Usage */}
          <Box>
            <Group gap="xs" mb={4}>
              <IconDownload size={16} color="#fd7e14" />
              <Text size="xs" c="dimmed">Data Usage</Text>
            </Group>
            <Group justify="space-between" mb={4}>
              <Text c="#2d2d2d" fw={500} size="sm">
                {voucherData.dataUsedMB || 0} MB
              </Text>
              <Text c="dimmed" size="xs">
                of {voucherData.dataLimitMB || 500} MB
              </Text>
            </Group>
            <Progress 
              value={dataPercentage} 
              color={dataPercentage > 80 ? 'red' : dataPercentage > 50 ? 'orange' : 'violet'}
              size="sm" 
              radius="xl"
            />
            <Text c={dataPercentage > 80 ? 'red' : 'dimmed'} size="xs" mt={4}>
              {dataPercentage}% used
            </Text>
          </Box>

          <Divider color="#e8e5f0" />

          {/* Time Remaining */}
          <Box>
            <Group gap="xs" mb={4}>
              <IconClock size={16} color="#be4bdb" />
              <Text size="xs" c="dimmed">Time Remaining</Text>
            </Group>
            <Text 
              c={voucherData.isExpired ? 'red' : voucherData.timeRemainingMinutes > 30 ? 'violet' : 'orange'}
              fw={700} 
              size="lg"
            >
              {voucherData.timeRemainingFormatted || 'Expired'}
            </Text>
            {voucherData.expiresAt && (
              <Text c="dimmed" size="xs" mt={4}>
                Expires: {new Date(voucherData.expiresAt).toLocaleString()}
              </Text>
            )}
          </Box>

          {/* Status Badge */}
          <Box mt="auto">
            <Divider color="#e8e5f0" mb="md" />
            <Group justify="center">
              <Badge 
                size="sm" 
                variant="dot"
                color={voucherData.isExpired ? 'red' : 'violet'}
              >
                {voucherData.isExpired ? 'Expired' : 'Active'}
              </Badge>
            </Group>
          </Box>
        </Stack>
      )}

      {!loading && !voucherData && !error && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          No voucher information available for this user.
        </Text>
      )}
    </Paper>
  );
}
